<template>
  <div v-if="editRow" ref="containerRef" @mouseup.stop @mousedown.stop>
    <UIInputSelect
      v-model="currentValue"
      :data
      size="sm"
      @ready="handleReady"
    />
  </div>
  <span v-else class="repository-collaborators-access" @click="handleClickEdit">
    {{ displayValue }}
    <UIPreloader v-if="isLoading" />
    <PencilIcon v-else-if="isEditable" aria-hidden="true" />
  </span>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'

import { RepositoryCollaborator, RepositoryStatus } from '@types'

import { ACCESS_TYPE_OWNER, ACCESS_TYPES } from '@/const'

import { handleCatchedError } from '@/helpers/common'
import { useNotifications } from '@/plugins/notification'

import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'

import { UIPreloader } from '@ui/core'
import { UIInputSelect } from '@ui/fields'
import { PencilIcon } from '@heroicons/vue/24/outline'

type Props = {
  item: RepositoryCollaborator
  editRow: boolean
  displayValue: string
  isReadonly: boolean
  repositoryId: string
}

type Emits = {
  (e: 'edit', value?: RepositoryCollaborator): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()

const { success } = useNotifications()

const containerRef = useTemplateRef('containerRef')

const currentValue = ref<string>()

const isLoading = ref(false)

const data = computed(() =>
  ACCESS_TYPES.filter((_, index) => index !== ACCESS_TYPE_OWNER - 1).map(
    (label, index) => ({
      value: `${index + 1}`,
      label,
    }),
  ),
)

const isEditable = computed(() => {
  return (
    !props.isReadonly &&
    props.item.access_type !== ACCESS_TYPE_OWNER &&
    props.item.status !== RepositoryStatus.PENDING
  )
})

const handleReady = (el: HTMLInputElement) => {
  el.focus()
}

const handleClickEdit = async () => {
  if (!isEditable.value) return
  currentValue.value = `${props.item.access_type}`
  emit('edit', props.item)
}

const handleUpdate = async () => {
  if (!currentValue.value || !props.repositoryId || !props.item.user?.id) return
  const id = props.item.user.id
  const access_type = +currentValue.value
  emit('edit', undefined)
  currentValue.value = undefined
  try {
    isLoading.value = true
    await repositoriesCollaboratorsStore.update(id, props.repositoryId, {
      access_type,
    })
    success({ message: 'Access rights have been successfully changed' })
  } catch (e) {
    handleCatchedError(e as string, { id })
  } finally {
    isLoading.value = false
  }
}

watch(currentValue, (value, oldValue) => {
  if (!oldValue || !value) return
  handleUpdate()
})

onClickOutside(containerRef, event => {
  const path =
    (event as any).path || (event.composedPath && event.composedPath())
  if (
    path.find((item: HTMLElement) =>
      item.classList?.contains('v-popper__popper'),
    )
  )
    return
  emit('edit', undefined)
  currentValue.value = undefined
})
</script>

<style scoped>
.repository-collaborators-access {
  @apply flex items-center;
  @apply gap-1;

  svg {
    @apply w-3 h-3;
    @apply hidden;
    @apply hover:text-indigo-500;
  }
}

[data-column='access_type']:hover {
  .repository-collaborators-access {
    svg {
      @apply block;
    }
  }
}
</style>
